import { DashboardTasksReturnReadable, TaskReadable } from '@crm/api'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useRolePermission } from '@crm/hooks/useRolePermission'
import { paths } from '@crm/routes'
import { UserRoleCRM } from '@crm/types'
import { abbreviateNumber, stringifyUrl } from '@crm/utils'
import { useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  DEFAULT_ISO_DATE_FORMAT,
  format,
  today,
  tomorrow,
  yesterday
} from '@shared/utils'
import { Card, Flex, Row, Typography } from 'antd'

import { useStyles } from './styles'
import TaskItem from './TaskItem'
import { TaskType } from './types'

const { Text } = Typography

type Props = {
  type: TaskType
  description?: string
} & DashboardTasksReturnReadable

export const TaskCard = ({ type, user, description }: Props) => {
  const { t } = useLocales()
  const { user: sessionUser, isAdmin } = useSessionContext()

  const hasPermission = useRolePermission([
    UserRoleCRM.manager,
    UserRoleCRM.admin
  ])

  const taskMap: Record<
    string,
    { total_tasks: number; tasks: TaskReadable[] }
  > = {
    expired: user?.past_tasks,
    future: user?.future_tasks,
    current: user?.current_tasks
  }

  const { total_tasks, tasks } = taskMap[type] ?? taskMap.current

  const navigate = useAppNavigate()
  const { styles } = useStyles({ type })

  const deadlineQuery: Record<TaskType, Record<string, string>> = {
    expired: {
      deadline: 'custom',
      deadline_end: format(yesterday, DEFAULT_ISO_DATE_FORMAT)
    },
    future: {
      deadline: 'custom',
      deadline_start: format(tomorrow, DEFAULT_ISO_DATE_FORMAT)
    },
    current: {
      deadline: 'today',
      deadline_end: format(today, DEFAULT_ISO_DATE_FORMAT),
      deadline_start: format(today, DEFAULT_ISO_DATE_FORMAT)
    }
  }

  const onClick = (taskType: TaskType): void => {
    const queryUser = {
      ...deadlineQuery[taskType],
      ...(hasPermission && !isAdmin && { owner: sessionUser?.user?.sso_id })
    }
    navigate(stringifyUrl(paths.tasks.root, queryUser))
  }

  return (
    <Card variant="borderless" className="card-body-no-padding">
      <Flex>
        <Flex
          className={styles.taskHeader}
          align="center"
          vertical
          justify="center"
          onClick={() => onClick(type)}
        >
          <h2 className={styles.taskHeaderText}>
            {total_tasks ? abbreviateNumber(total_tasks) : 0}
          </h2>

          <Text className={styles.taskHeaderText} strong>
            {t('menu.tasks').toUpperCase()}
          </Text>

          {description && <small>{description}</small>}
        </Flex>

        <Row className={styles.taskList}>
          {tasks?.map((item) => (
            <TaskItem key={item?.id} data={item} taskType={type} />
          ))}
        </Row>
      </Flex>
    </Card>
  )
}
